import { ArrowUp, ArrowDown, Trash2, Search } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http, errMsg, formatDate } from "@/lib/api";

export const inputCls = "mt-1 w-full rounded-xl border border-[#DCEAE6] bg-white px-3 py-2 text-sm text-nanina-ink outline-none focus:ring-2 focus:ring-nanina-sky/50";

export const F = ({ label, children, hint, className = "" }) => (
  <label className={`block text-xs font-semibold text-nanina-green ${className}`}>
    {label}
    {children}
    {hint && <span className="mt-1 block text-[11px] font-normal text-nanina-muted">{hint}</span>}
  </label>
);

const PILL = {
  published: ["Terbit", "bg-nanina-freshsoft text-nanina-green"],
  unpublished: ["Disembunyikan", "bg-nanina-peach/40 text-[#7A3B2E]"],
  draft: ["Draft", "bg-gray-100 text-gray-600"],
};

export const StatusPill = ({ item }) => {
  const [label, cls] = PILL[item.status] || PILL.draft;
  return (
    <span className="inline-flex flex-wrap gap-1">
      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${cls}`} data-testid={`status-pill-${item.id}`}>{label}</span>
      {item.status === "published" && item.has_changes && (
        <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-nanina-butter/60 text-nanina-green" data-testid={`changes-pill-${item.id}`}>Ada perubahan belum terbit</span>
      )}
    </span>
  );
};

export const PublishControls = ({ item, coll, reload, testId }) => {
  const act = async (a) => {
    try {
      await http.post(`/admin/content/${coll}/${item.id}/${a}`);
      toast.success(a === "publish" ? "Diterbitkan" : "Disembunyikan dari website");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const needsPublish = item.status !== "published" || item.has_changes;
  return (
    <div className="flex items-center gap-1">
      {needsPublish && (
        <button onClick={() => act("publish")} className="rounded-full bg-nanina-green text-white text-[11px] font-semibold px-3 py-1.5" data-testid={`${testId}-publish`}>
          {item.status === "published" ? "Terbitkan perubahan" : "Terbitkan"}
        </button>
      )}
      {item.status === "published" && (
        <button onClick={() => act("unpublish")} className="rounded-full border border-nanina-line text-nanina-green text-[11px] font-semibold px-3 py-1.5" data-testid={`${testId}-unpublish`}>Sembunyikan</button>
      )}
    </div>
  );
};

export const FormButtons = ({ busy, onPublish, testId }) => (
  <div className="sm:col-span-2 flex flex-col sm:flex-row gap-2">
    <button type="submit" disabled={busy} className="flex-1 rounded-full border border-nanina-green text-nanina-green text-sm font-semibold py-2.5 disabled:opacity-60" data-testid={`${testId}-save-button`}>{busy ? "Menyimpan..." : "Simpan Draft"}</button>
    <button type="button" disabled={busy} onClick={(e) => { if (e.currentTarget.form.reportValidity()) onPublish(); }} className="flex-1 rounded-full bg-nanina-green text-white text-sm font-semibold py-2.5 disabled:opacity-60" data-testid={`${testId}-publish-button`}>Simpan & Terbitkan</button>
  </div>
);

export async function saveItem(coll, item, body, publish, onCreated) {
  const { data } = item.id ? await http.put(`/admin/${coll}/${item.id}`, body) : await http.post(`/admin/${coll}`, body);
  if (!item.id) onCreated?.(data);
  if (publish) await http.post(`/admin/content/${coll}/${data.id}/publish`);
  return data;
}

export const Card = ({ title, children }) => (
  <section className="rounded-2xl bg-white border border-nanina-line p-4 sm:p-5">
    <h3 className="text-sm font-bold text-nanina-green mb-3">{title}</h3>
    <div className="grid sm:grid-cols-2 gap-3">{children}</div>
  </section>
);

export const SaveBar = ({ busy, s, onDraft, onPublish, testId }) => (
  <div className="fixed bottom-0 inset-x-0 lg:left-64 z-20 bg-white/95 backdrop-blur border-t border-nanina-line">
    <div className="mx-auto max-w-5xl px-4 py-3 flex flex-wrap items-center justify-between gap-2">
      <p className="text-[11px] text-nanina-muted" data-testid={`${testId}-publish-state`}>
        {s.has_changes ? "Ada perubahan belum terbit" : "Semua perubahan sudah terbit"}{s.published_at ? ` · Terakhir terbit ${formatDate(s.published_at)}` : ""}
      </p>
      <div className="flex gap-2">
        <Link to="/admin/preview" target="_blank" className="rounded-full px-3 py-2 text-xs font-semibold text-nanina-green hover:bg-nanina-bg" data-testid={`${testId}-preview-link`}>Pratinjau</Link>
        <button onClick={onDraft} disabled={busy} className="rounded-full border border-nanina-green text-nanina-green text-xs sm:text-sm font-semibold px-4 py-2 disabled:opacity-60" data-testid={`${testId}-save-button`}>Simpan Draft</button>
        <button onClick={onPublish} disabled={busy} className="rounded-full bg-nanina-green text-white text-xs sm:text-sm font-semibold px-4 py-2 disabled:opacity-60" data-testid={`${testId}-publish-button`}>Simpan & Terbitkan</button>
      </div>
    </div>
  </div>
);

export const ConfirmDelete = ({ onConfirm, label, testId }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button className="p-2 rounded-lg text-red-600 hover:bg-red-50" aria-label={`Hapus ${label}`} data-testid={testId}><Trash2 className="h-4 w-4" /></button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Hapus "{label}"?</AlertDialogTitle>
        <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="confirm-delete-cancel">Batal</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-ok">Hapus</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export async function moveItem(list, index, dir, endpoint) {
  const j = index + dir;
  if (j < 0 || j >= list.length) return false;
  const next = [...list];
  [next[index], next[j]] = [next[j], next[index]];
  await http.post(endpoint, next.map((x, i) => ({ id: x.id, order: i })));
  return true;
}

export const MoveButtons = ({ onUp, onDown, testId }) => (
  <div className="flex">
    <button onClick={onUp} className="p-2 rounded-lg hover:bg-nanina-bg" aria-label="Naikkan urutan" data-testid={`${testId}-up`}><ArrowUp className="h-4 w-4" /></button>
    <button onClick={onDown} className="p-2 rounded-lg hover:bg-nanina-bg" aria-label="Turunkan urutan" data-testid={`${testId}-down`}><ArrowDown className="h-4 w-4" /></button>
  </div>
);

export const ListToolbar = ({ q, setQ, status, setStatus, children, testId }) => (
  <div className="flex flex-col sm:flex-row gap-2 mb-3" data-testid={`${testId}-toolbar`}>
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nanina-muted" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className={`${inputCls} !mt-0 pl-9`} data-testid={`${testId}-search`} />
    </div>
    <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !mt-0 sm:w-44`} data-testid={`${testId}-status-filter`}>
      <option value="">Semua status</option>
      <option value="published">Terbit</option>
      <option value="draft">Draft</option>
      <option value="unpublished">Disembunyikan</option>
    </select>
    {children}
  </div>
);

export const matches = (item, q, status, fields) =>
  (!status || item.status === status) && (!q || fields.some((f) => String(item[f] || "").toLowerCase().includes(q.toLowerCase())));

export const PanelHead = ({ text, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
    <p className="text-sm text-nanina-muted">{text}</p>
    {action}
  </div>
);

export const AddButton = ({ onClick, label = "Tambah", testId }) => (
  <button onClick={onClick} className="shrink-0 self-start inline-flex items-center gap-1.5 rounded-full bg-nanina-green text-white text-sm font-semibold px-4 py-2 hover:bg-nanina-greendark transition-colors" data-testid={testId}>+ {label}</button>
);

export const linesToList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
